import { useCallback, useMemo, useRef } from "react";
import { useWatch, type Control } from "react-hook-form";

// A primitive-valued form field -- see the String()-comparison note below for why
// non-primitive values (File, Date, plain objects) are deliberately excluded from this
// hook's contract rather than merely documented as unsupported.
type Primitive = string | number | boolean | null | undefined;

// react-hook-form's own `isDirty` does not reset to `false` when a field is edited and
// then typed back to its original value -- it stays true once any field has been
// touched. That makes it an unreliable sole signal for "did anything actually change"
// on a form that hydrates itself from fetched/passed-in data (see ui-consistency.md
// SS10 gotcha 9). This hook adds a second, independent signal: snapshot the loaded
// values once (via `setSnapshot`), then compare the live watched values against that
// snapshot on every render. Callers should AND this with `isDirty`, not replace it --
// see EditProperty.tsx / AddUnitForm.tsx for the canonical usage. Track a File/image/
// Date field via RHF's own `dirtyFields.<name>` instead (both call sites already do this
// for their image field) -- the type constraint below only accepts primitive-valued
// fields, since the comparison is String()-based (see below) and would otherwise
// silently treat any two distinct non-primitive values as equal.
export function useFieldChangesSnapshot<TValues extends Record<string, Primitive>>(
  control: Control<any>,
  fieldNames: (keyof TValues & string)[]
) {
  const originalValuesRef = useRef<Partial<TValues> | null>(null);
  // Re-derive a stable array identity from the field names' own content rather than
  // trusting the caller to memoize the array literal it passes in -- react-hook-form's
  // useWatch resubscribes whenever the `name` array's identity changes, so an inline
  // literal at the call site would otherwise tear down and rebuild the subscription
  // every render.
  const stableFieldNames = useMemo(() => fieldNames, [fieldNames.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  const watched = useWatch({ control, name: stableFieldNames as any }) as any[];

  // String-normalized comparison: a numeric/select-backed field (e.g. a size or id)
  // can come back from the snapshot as a real number but from a text input's onChange
  // as a string once the user has ever interacted with it, even reverting to the exact
  // same digits -- a strict `!==` would then report "changed" forever after that first
  // interaction. String() makes 500 and "500" compare equal while still catching any
  // genuine content difference.
  const hasFieldChanges = originalValuesRef.current
    ? stableFieldNames.some((name, i) => String(watched[i] ?? "") !== String(originalValuesRef.current![name] ?? ""))
    : false;

  const setSnapshot = useCallback((values: Partial<TValues>) => {
    originalValuesRef.current = values;
  }, []);

  return { hasFieldChanges, setSnapshot };
}
